// src/components/useCollapseOnScroll.js
// Session 18 — scroll-collapse helper with post-flip lockout.
//
// Session 16 caused header "shake" from micro-scroll toggling at the threshold.
// Session 17 added 30px hysteresis + rAF throttling — but the shake came back
// on Android/PC for a different reason:
//
//   The collapsed bar is MUCH shorter than the expanded filter block (~44px vs
//   ~200px+). When the block collapses, every row below it jumps up by that
//   height difference, which moves the scroller's scrollTop. That self-induced
//   scroll can land back below the threshold -> un-collapse -> block grows ->
//   content jumps down -> scrollTop crosses the threshold again -> collapse...
//   an endless oscillation. 30px of hysteresis can't absorb a 150px+ layout
//   shift, so it never stuck.
//
// This version:
//   - rAF throttling (one update per paint frame) — kept from Session 17
//   - Wider 80px hysteresis — kept idea, bigger band
//   - NEW: post-flip lockout. After collapsed toggles, the layout is resizing
//     and scrollTop is unreliable, so we ignore scroll position for LOCKOUT_MS.
//     This breaks the feedback loop no matter how big the height difference is.
//   - Bails out early if state wouldn't actually change
//
// IMPORTANT: This app uses a <main className="app-content"> container with
// position:fixed + overflow-y:auto as the scroll container (NOT the window).
// So we attach the scroll listener to that element. Falls back to window if
// the container isn't found yet.

import { useEffect, useState } from 'react';

export function useCollapseOnScroll(threshold = 120) {
  const [collapsed, setCollapsed] = useState(false);
  useEffect(() => {
    const scroller = document.querySelector('.app-content') || window;
    const getY = () => scroller === window ? window.scrollY : scroller.scrollTop;

    // Hysteresis: collapse at `threshold`, un-collapse at `threshold - HYSTERESIS`.
    const HYSTERESIS = 80;
    // After a flip, ignore scroll position for this long while layout settles.
    const LOCKOUT_MS = 350;

    let ticking = false;
    let currentCollapsed = false;
    let lockedUntil = 0;

    const update = () => {
      ticking = false;

      // Layout is still settling from the last flip — scrollTop is unreliable.
      if (Date.now() < lockedUntil) return;

      const y = getY();
      let next = currentCollapsed;
      if (!currentCollapsed && y > threshold) next = true;
      else if (currentCollapsed && y < threshold - HYSTERESIS) next = false;

      if (next !== currentCollapsed) {
        currentCollapsed = next;
        lockedUntil = Date.now() + LOCKOUT_MS;
        setCollapsed(next);
      }
    };

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    };

    scroller.addEventListener('scroll', onScroll, { passive: true });
    update();
    return () => scroller.removeEventListener('scroll', onScroll);
  }, [threshold]);
  return collapsed;
}

export default useCollapseOnScroll;
